import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import type { LedgerAccount, LedgerAccountInput } from "@/types/ledger";

const ACCOUNT_TYPES: LedgerAccountInput["type"][] = [
  "asset",
  "liability",
  "equity",
  "income",
  "expense",
];

type Props = {
  accounts: LedgerAccount[];
  onCreate: (payload: LedgerAccountInput) => Promise<void>;
};

export function LedgerAccountsTable({ accounts, onCreate }: Props) {
  const [code, setCode] = useState("1000");
  const [name, setName] = useState("");
  const [type, setType] = useState<LedgerAccountInput["type"]>("asset");
  const [currency, setCurrency] = useState("USD");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sortedAccounts = useMemo(() => {
    return [...accounts].sort((a, b) => a.code.localeCompare(b.code));
  }, [accounts]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!code.trim() || !name.trim()) {
      setError("Account code and name are required");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onCreate({ code: code.trim(), name: name.trim(), type, currency: currency.toUpperCase() });
      setCode("1000");
      setName("");
      setType("asset");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create account");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-background/60 shadow-inner shadow-black/40">
      <div className="border-b border-border px-5 py-4">
        <h2 className="text-lg font-semibold text-primary-foreground dark:text-amber-200">Chart of accounts</h2>
        <p className="text-xs text-muted-foreground">Track the accounts available for posting entries.</p>
      </div>
      <div className="max-h-[520px] overflow-x-auto overflow-y-auto px-5 py-4">
        {sortedAccounts.length === 0 ? (
          <p className="text-xs text-muted-foreground">No accounts yet. Create one below.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="py-2">Code</th>
                <th className="py-2">Name</th>
                <th className="py-2">Type</th>
                <th className="py-2 text-right">Currency</th>
              </tr>
            </thead>
            <tbody>
              {sortedAccounts.map((account) => (
                <tr key={account.id} className="border-t border-border/60 text-foreground/80">
                  <td className="py-2 font-mono text-xs text-muted-foreground">{account.code}</td>
                  <td className="py-2">{account.name}</td>
                  <td className="py-2 text-xs capitalize text-muted-foreground">{account.type}</td>
                  <td className="py-2 text-right text-xs text-muted-foreground">{account.currency}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <form onSubmit={handleSubmit} className="mt-5 space-y-3 rounded-2xl border border-border/80 bg-background/40 p-4 text-xs">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Add account</div>
          {error && (
            <div className="rounded-lg border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-rose-100">
              {error}
            </div>
          )}
          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-foreground/70">
              <span className="mb-1 block text-[11px] uppercase tracking-wide text-muted-foreground">Code</span>
              <input
                type="text"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                placeholder="1000"
              />
            </label>
            <label className="text-foreground/70">
              <span className="mb-1 block text-[11px] uppercase tracking-wide text-muted-foreground">Name</span>
              <input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                placeholder="Cash on hand"
              />
            </label>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <label className="text-foreground/70">
              <span className="mb-1 block text-[11px] uppercase tracking-wide text-muted-foreground">Type</span>
              <select
                value={type}
                onChange={(event) => setType(event.target.value as LedgerAccountInput["type"])}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
              >
                {ACCOUNT_TYPES.map((entry) => (
                  <option key={entry} value={entry}>
                    {entry}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-foreground/70">
              <span className="mb-1 block text-[11px] uppercase tracking-wide text-muted-foreground">Currency</span>
              <input
                type="text"
                value={currency}
                maxLength={3}
                onChange={(event) => setCurrency(event.target.value.toUpperCase())}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
              />
            </label>
          </div>
          <div className="flex justify-end">
            <Button type="submit" size="sm" disabled={submitting}>
              {submitting ? "Saving…" : "Add account"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
